import type { ProductMarkLite } from "@/types/product";
import { get, post, type ApiResponse } from "@/utils/fetch";

// ============================================
// 定制AI商品相关 API 接口
// ============================================

/** 定制区域坐标（百分比） */
export interface CustomArea {
  /** 定制区域左上角X坐标(%) */
  x?: number;
  /** 定制区域左上角Y坐标(%) */
  y?: number;
  /** 定制区域宽度(%) */
  width?: number;
  /** 定制区域高度(%) */
  height?: number;
}

/** 区域尺寸（宽高） */
export interface RegionSize {
  w?: number;
  h?: number;
}

/** AI应用关联配置（dto.AIProductLinkConfig） */
export interface AiAppConfig {
  /** AI应用类型(1-3D手办,2/8-16冰箱贴,5-AI手机壳,6-平面商品) */
  type_code?: number;
  /** AI应用类型名称 */
  type_name?: string;
  /** 正面模板图片URL */
  front_tpl_img_url?: string;
  /** 正面定制区域（百分比坐标） */
  front_custom_area?: CustomArea;
  /** 正面商品图 URL */
  front_product_image_url?: string;
  /** 正面定制区域图 URL */
  front_custom_area_image_url?: string;
  /** 正面区域尺寸(像素) */
  front_region_px?: RegionSize;
  /** 正面实际尺寸(厘米) */
  front_size_cm?: RegionSize;
  /** 背面模板图片URL（可选） */
  back_tpl_img_url?: string;
  /** 背面定制区域（可选） */
  back_custom_area?: CustomArea;
  /** 背面商品图 URL */
  back_product_image_url?: string;
  /** 背面定制区域图 URL */
  back_custom_area_image_url?: string;
  /** 背面区域尺寸(像素) */
  back_region_px?: RegionSize;
  /** 背面实际尺寸(厘米) */
  back_size_cm?: RegionSize;
  /** 3D手办/冰箱贴生成图 URL */
  generate_image_url?: string;
}

/** 商品服务信息 */
interface AiProductService {
  /** 服务ID */
  id?: number;
  /** 服务名称 */
  name?: string;
  /** 服务描述 */
  desc?: string;
  /** 服务Icon URL */
  service_icon?: string;
}

/** AI商品列表项 */
export interface AiProductListItem {
  /** 商品ID */
  id?: number;
  /** 商品名称 */
  name?: string;
  /** 货号 */
  article_number?: string;
  /** SPU编码 */
  spu?: string;
  /** 分类ID */
  category_id?: number;
  /** 主图URL（优先返回低分辨率） */
  image_url?: string;
  /** 第二张图URL */
  second_image_url?: string;
  /** 最低供货价 */
  min_price?: number;
  /** 最高供货价 */
  max_price?: number;
  /** 价格前缀：1-正常价格，2-预估价格 */
  estimated_price_label_status?: 1 | 2;
  /** 商品素材 */
  material?: string;
  /** 商品类型 */
  product_type?: number;
  /** AI应用配置关联类型：0=未设置, 1=关联SPU, 2=关联SKU */
  ai_app_link_type?: number;
  /** 排序 */
  sort?: number;
  /** 关联AI应用完整配置（ai_app_link_type=1 时在此返回） */
  ai_app_config?: AiAppConfig;
  /** 商品服务列表 */
  services?: AiProductService[];
  /** C 端是否显示销量（1:显示, 2:不显示） */
  is_visible?: number;
  /** 真实销量 */
  real_sales?: number;
  /** 商品标记（v1.3.1） */
  markers?: ProductMarkLite[];
  /** 商品优势说明（v1.3.1） */
  advantages?: string[];
  /** 登录用户是否已收藏 */
  is_favorited?: boolean;
}

/** AI商品列表请求参数 */
export interface AiProductListRequest {
  /** 分类ID列表 */
  category_ids?: number[];
  /** 搜索关键词 */
  keyword?: string;
  /** 页码 */
  page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 服务ID列表（可选，多选OR逻辑） */
  service_ids?: number[];
  /** 商品标记ID列表，多选 OR 逻辑 */
  marker_ids?: number[];
  /** 排序：price_asc / price_desc / sales_asc / sales_desc，空为默认 */
  sort_by?: string;
}

/** AI商品列表响应 */
export interface AiProductListResponse {
  /** 商品列表 */
  list?: AiProductListItem[];
  /** 当前页码 */
  page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 总数 */
  total?: number;
}

/**
 * 获取定制AI商品列表
 * @param params 查询参数
 */
export const getAiProductList = async (
  params: AiProductListRequest = {},
): Promise<ApiResponse<AiProductListResponse>> => {
  const defaultParams: AiProductListRequest = {
    page: 1,
    page_size: 20,
    ...params,
  };
  return post<AiProductListResponse>(
    "/api/v1/supplier/product/ai-app/list",
    defaultParams,
  );
};

// ============================================
// AI 商品配置相关
// ============================================

/** AI 商品 C 端模型 */
export interface AiProductModelExtra {
  simple_repair_price?: number;
  fine_repair_price?: number;
}

export interface AiProductClientModel {
  id?: number;
  model_id_str?: string;
  model_name?: string;
  model_image?: string;
  /** 模型参数，调用模型时带上 */
  model_params?: string;
  /** 0-收费 1-免费 */
  is_free?: number;
  /** 消耗算力（is_free=0 时有效） */
  compute_power?: number;
  sort?: number;
  extra?: AiProductModelExtra;
}

/** AI 商品 C 端素材 */
export interface AiProductClientMaterial {
  id?: number;
  image_url?: string;
}

/** AI 商品 C 端配置响应 */
export interface AiProductClientConfig {
  /** 1-3D手办 2/8-16冰箱贴 5-AI手机壳 6-平面商品 */
  type_code?: number;
  name?: string;
  icon_url?: string;
  front_image_url?: string;
  back_image_url?: string;
  /** 该类型下已上架的模型（手办/冰箱贴/手机壳等） */
  models?: AiProductClientModel[];
  /** 按 material_type 分组的素材 */
  materials?: Record<string, AiProductClientMaterial[]>;
  /** 图片标注：seaArt 线稿图片 url */
  seaart_draft_image?: string;
  /** 图片标注：seaArt 线稿序号 */
  seaart_draft_index?: number;
  /** 图片标注：seaArt 效果图 url */
  seaart_effect_image?: string;
  /** 图片标注：seaArt 效果图序号 */
  seaart_effect_index?: number;
  /** type_code=5 手机壳：服务端文字案例列表 */
  text_case_texts?: string[];
}

/**
 * 获取 AI 商品配置（C 端）
 * 传 typeCode 返回该类型完整配置；不传返回全部类型基础信息
 */
export const getAiProductConfig = (typeCode?: number) => {
  return get<AiProductClientConfig[]>("/api/v1/supplier/ai-product/config", {
    params: typeCode ? { type_code: typeCode } : undefined,
  });
};

// ============================================
// AI 生成（手办/冰箱贴/手机壳）
// ============================================

/**
 * 创建快应用请求
 * - 手办/冰箱贴(1/2)：通常需 original_image、app_id、model_name
 * - 手机壳(5)：必填 phone_model；prompt 与 original_image 按模型：图模式可仅传参考图（prompt 可空串）
 */
export interface CreateQuickAppRequest {
  /** AI 商品类型：1/2/8-16 手办与冰箱贴，5 手机壳，6 平面商品 */
  type_code?: number;
  /** 手办/冰箱贴：原始图 URL；手机壳可选 */
  original_image?: string;
  /** 模型主键 ai_product_models.id */
  model_id?: number;
  /** 模型名称（生成记录展示） */
  model_name?: string;
  /** 手机壳：提示词（文字模式必填；图片模式可与参考图配合，可为空） */
  prompt?: string;
  /** 手机壳：适配机型字符串（必填） */
  phone_model?: string;
  /** SeaArt 风格：1-写实 2-动漫 3-Q版 */
  sea_art_style?: number;
  /** 用户描述 */
  user_description?: string;
  /** 世界杯冰箱贴：国家球队中文名 */
  team?: string;
  /** 月相冰箱贴：模板类型 single/double/triple */
  template?: string;
  /** 月相冰箱贴：主日期，YYYY-MM-DD */
  date?: string;
  /** 月相冰箱贴：第二个日期，YYYY-MM-DD */
  date2?: string;
  /** 月相冰箱贴：第三个日期，YYYY-MM-DD */
  date3?: string;
  /** 月相冰箱贴：顶部/底部背景颜色，如 #000000 */
  bg_color?: string;
  /** 月相冰箱贴：月球背景 */
  moon_bg?: string;
  /** 月相冰箱贴：后端文本数组 */
  texts?: string[];
  /** 商品 sku */
  sku?: string;
  /** 冰箱贴刻字文案，预留多行 */
  engraving_texts?: string[];
}

/** 月相冰箱贴生成记录 extra.moon_phase */
export interface MoonPhaseRecordExtra {
  template?: string;
  date?: string;
  date2?: string;
  date3?: string;
  bg_color?: string;
  moon_bg?: string;
  texts?: string[];
}

/** 创建快应用响应 */
export interface CreateQuickAppResponse {
  /** 异步任务 ID；月相冰箱贴使用该 ID 轮询 POST application/task-result */
  id?: string;
  /** AI 商品生成记录主键；月相冰箱贴加入采购车时作为 creation_id */
  record_id?: number;
}

/**
 * 创建快应用（手办/冰箱贴/手机壳 AI 生成）
 */
export const createQuickApp = (params: CreateQuickAppRequest) => {
  return post<CreateQuickAppResponse>(
    "/api/v1/supplier/ai-product/application/create-quick-app",
    params,
  );
};

/** 获取任务结果请求 */
export interface GetTaskResultRequest {
  /** 任务 ID（必填） */
  task_id: string;
  /** 商品类型（1/2/5/6 等），传入可自动保存生成记录 */
  type_code?: number;
  /** 原始图片 URL */
  original_image?: string;
  /** 模型名称 */
  model_name?: string;
}

/** 获取任务结果响应 */
export interface GetTaskResultResponse {
  task_id?: string;
  /** 1-等待中 2-处理中 3-成功 4-失败 */
  status?: number;
  /** 图片 URL 列表（status=3 时返回） */
  result?: string[];
  /** 错误信息（status=4 时返回） */
  error_msg?: string;
  /** 手机壳成功：平面图 */
  plane_image_url?: string;
  /** 手机壳成功：套壳效果图 */
  effect_image_url?: string;
}

/**
 * 获取任务结果（轮询）
 * @description B2B「AI 定制商品」页已改为仅用 `POST .../record/list` + `ids` 轮询；其它场景若仍依赖异步任务 ID 可使用本接口。
 */
export const getTaskResult = (params: GetTaskResultRequest) => {
  return post<GetTaskResultResponse>(
    "/api/v1/supplier/ai-product/application/get-task-result",
    params,
  );
};

/**
 * 获取应用任务结果（月相冰箱贴等新任务轮询接口）
 */
export const getApplicationTaskResult = (params: GetTaskResultRequest) => {
  return post<GetTaskResultResponse>(
    "/api/v1/supplier/ai-product/application/get-task-result",
    params,
  );
};

// ============================================
// 平面商品生成记录（原 T 恤 / 卫衣）
// ============================================

/** 创建平面商品生成记录请求 */
export interface CreateTshirtHoodieRecordReq {
  /** 6-平面商品（原 T 恤 / 卫衣合并） */
  type_code: 6;
  /** 商品 SKU */
  sku?: string;
  /** 用户上传原图 URL 数组（旧字段，兼容；新字段都不传时后端按正面处理） */
  original_image_urls?: string[];
  /** 正面用户原素材 URL 数组 */
  front_original_image_urls?: string[];
  /** 背面用户原素材 URL 数组 */
  back_original_image_urls?: string[];
  /** 正面商品图 URL */
  front_product_image_url?: string;
  /** 正面定制区域图（印花图）URL */
  front_custom_area_image_url?: string;
  /** 正面区域宽度(像素) */
  front_region_w?: number;
  /** 正面区域高度(像素) */
  front_region_h?: number;
  /** 正面定制区域实际宽度(厘米) */
  front_size_w_cm?: number;
  /** 正面定制区域实际高度(厘米) */
  front_size_h_cm?: number;
  /** 正面印花宽度(厘米) */
  front_print_w_cm?: number;
  /** 正面印花高度(厘米) */
  front_print_h_cm?: number;
  /** 背面商品图 URL */
  back_product_image_url?: string;
  /** 背面定制区域图（印花图）URL */
  back_custom_area_image_url?: string;
  /** 背面区域宽度(像素) */
  back_region_w?: number;
  /** 背面区域高度(像素) */
  back_region_h?: number;
  /** 背面定制区域实际宽度(厘米) */
  back_size_w_cm?: number;
  /** 背面定制区域实际高度(厘米) */
  back_size_h_cm?: number;
  /** 背面印花宽度(厘米) */
  back_print_w_cm?: number;
  /** 背面印花高度(厘米) */
  back_print_h_cm?: number;
}

/** 创建平面商品生成记录响应 */
export interface CreateTshirtHoodieRecordResp {
  id?: number;
}

/**
 * 创建平面商品生成记录（接口路径仍为 create-tshirt-hoodie）
 */
export const createTshirtHoodieRecord = (
  params: CreateTshirtHoodieRecordReq,
) => {
  return post<CreateTshirtHoodieRecordResp>(
    "/api/v1/supplier/ai-product/record/create-tshirt-hoodie",
    params,
  );
};

// ============================================
// AI 生成记录
// ============================================

/** 区域尺寸 */
export interface AiProductRegionSize {
  w?: number;
  h?: number;
}

/** 手办/冰箱贴：草稿 / 效果图；手机壳：plane / effect（与 Swagger 一致） */
export interface AiProductRecordGeneratedImages {
  draft?: string;
  /** 世界杯冰箱贴：明信片图 */
  postcard?: string;
  effect?: string;
  /** 手机壳：平面图 */
  plane?: string;
  /** 手机壳：PSD 源文件 */
  psd?: string;
  /** 月相冰箱贴：高清海报 */
  poster?: string;
}

/** 生成记录扩展数据 */
export interface AiProductRecordExtra {
  /** 3D手办/冰箱贴的生成图 URL */
  generate_image_url?: string;
  /** 手办/冰箱贴：草稿与效果图；手机壳：plane/effect/psd */
  generated_images?: AiProductRecordGeneratedImages;
  /** 手机壳：用户提示词 */
  user_prompt?: string;
  /** 手机壳：适配机型 */
  phone_model?: string;
  /** 世界杯冰箱贴：国家球队中文名 */
  team?: string;
  /** 月相冰箱贴：创建时保存的模板、日期和背景信息 */
  moon_phase?: MoonPhaseRecordExtra;
  /** 冰箱贴刻字文案，预留多行 */
  engraving_texts?: string[];
  /** 失败原因 */
  error_msg?: string;
  /** 平面商品：正面商品图 */
  front_product_image_url?: string;
  /** 正面定制区域图 */
  front_custom_area_image_url?: string;
  /** 正面区域尺寸(像素) */
  front_region_px?: AiProductRegionSize;
  /** 正面实际尺寸(厘米) */
  front_size_cm?: AiProductRegionSize;
  /** 背面商品图 */
  back_product_image_url?: string;
  /** 背面定制区域图 */
  back_custom_area_image_url?: string;
  /** 背面区域尺寸(像素) */
  back_region_px?: AiProductRegionSize;
  /** 背面实际尺寸(厘米) */
  back_size_cm?: AiProductRegionSize;
  /** 平面商品：正面用户原素材 URL 列表（详情/列表在 extra 下） */
  front_original_image_urls?: string[];
  /** 平面商品：背面用户原素材 URL 列表 */
  back_original_image_urls?: string[];
  // ── 手机壳画布坐标（以下坐标原点均为画布左上角，单位 px）──
  /** 画布宽度 */
  canvas_w?: number;
  /** 画布高度 */
  canvas_h?: number;
  /** 模板图在画布中的左上角 x */
  tpl_x?: number;
  /** 模板图在画布中的左上角 y */
  tpl_y?: number;
  /** 模板图宽度 */
  template_w?: number;
  /** 模板图高度 */
  template_h?: number;
  /** 平面图在画布中的左上角 x（旋转前，可为负） */
  img_x?: number;
  /** 平面图在画布中的左上角 y（旋转前，可为负） */
  img_y?: number;
  /** 平面图宽度 */
  img_w?: number;
  /** 平面图高度 */
  img_h?: number;
  /** 平面图旋转角度（度，顺时针，旋转轴为图片中心） */
  rotation?: number;
}

/** AI 生成记录列表项 */
export interface AiProductRecordItem {
  id?: number;
  /**  */
  status?: number;
  /** 1-3D手办 2/8-16冰箱贴 5-AI手机壳 6-平面商品 */
  type_code?: number;
  /** 模型侧 ID 字符串 */
  model_id_str?: string;
  model_name?: string;
  /** 用户手机号（若有） */
  phone?: string;
  /** 原始图 URL（单条，兼容旧字段） */
  original_image_url?: string;
  /** 原始图 URL 列表（旧字段，兼容） */
  original_image_urls?: string[];
  /** 生成商品图 URL 列表 */
  product_images?: string[];
  /** 草稿图 URL 列表 */
  draft_images?: string[];
  /** 类型差异化数据 */
  extra?: AiProductRecordExtra;
  /** 创建时间（毫秒时间戳） */
  create_at?: number;
  /** 商品sku */
  sku?: string;
  /** 商品spu */
  spu?: string;
  /** 图生模型任务记录 ID（用于恢复轮询进度、克重估算入参） */
  model_generation_record_id?: number;
  /** 保存至我的商品的时间戳（毫秒），>0 表示已保存 */
  my_product_at?: number;
}

/** AI 生成记录列表请求（POST JSON，与 OpenAPI AIProductRecordListReq 一致） */
export interface AiRecordListRequest {
  page: number;
  page_size: number;
  /**
   * 商品类型：1=3D手办 2/8-16=冰箱贴 5=AI手机壳 6=平面商品
   * 可多选（OR）；不传或空表示不限类型。可传单个数字，请求体会规范为数组。
   */
  type_code?: number | number[];
  /**
   * 按生成记录主键 id 批量筛选（多选，等价 id IN (...)）
   */
  ids?: number[];
}

/** AI 生成记录列表响应 */
export interface AiRecordListResponse {
  list?: AiProductRecordItem[];
  page?: number;
  page_size?: number;
  total?: number;
}

/** 构造 POST /record/list 请求体（page_size 限制 1–100） */
export function buildAiRecordListBody(
  params: AiRecordListRequest,
): Record<string, unknown> {
  const page = Math.max(1, Math.floor(Number(params.page)) || 1);
  const rawSize = Math.floor(Number(params.page_size)) || 20;
  const page_size = Math.min(100, Math.max(1, rawSize));
  const body: Record<string, unknown> = { page, page_size };
  if (params.type_code !== undefined) {
    const codes = (
      Array.isArray(params.type_code) ? params.type_code : [params.type_code]
    )
      .map((c) => Math.floor(Number(c)))
      .filter((c) => Number.isFinite(c) && c >= 1);
    if (codes.length > 0) body.type_code = codes;
  }
  if (params.ids !== undefined && params.ids.length > 0) {
    const ids = params.ids
      .map((id) => Math.floor(Number(id)))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length > 0) body.ids = ids;
  }
  return body;
}

/**
 * 获取 AI 生成记录列表（POST JSON）
 */
export const getAiRecordList = (params: AiRecordListRequest) => {
  return post<AiRecordListResponse>(
    "/api/v1/supplier/ai-product/record/list",
    buildAiRecordListBody(params),
  );
};

/**
 * 获取 AI 生成记录详情
 * @param id 记录 ID
 */
export const getAiRecordDetail = (id: number) => {
  return get<AiProductRecordItem>("/api/v1/supplier/ai-product/record/detail", {
    params: { id },
  });
};

/**
 * 删除 AI 生成记录
 * @param id 记录 ID
 */
export const deleteAiRecord = (id: number) => {
  return post("/api/v1/supplier/ai-product/record/delete", { id });
};

// ============================================
// AI 定制配置（3D 手办 / 手机壳等，按 ai_code 区分）
// ============================================

/**
 * 定制配置 / 算力 preview 接口 ai_code 字符串值
 */
export const AI_CUSTOMIZATION_AI_CODE = {
  /** 3D 手办图生模型 */
  MODEL_GEN: "model_gen",
  /** AI 手机壳补全 */
  PHONE_CASE: "phone_case",
} as const;

/** GET /api/v1/supplier/ai-product/customization/config 返回的 data */
export interface AICustomizationConfigData {
  ai_code?: number;
  print_material_unit_price?: number;
  support_material_unit_price?: number;
  profit_coefficient?: number;
  /** 是否免费：0=否，1=是 */
  model_gen_is_free?: number;
  /** 消耗算力（点/次） */
  model_gen_compute_power?: number;
}

/**
 * 获取 AI 应用定制配置（卖家登录）
 * @param aiCode 应用标识（整数），与 type_code 一致
 */
export const getAiCustomizationConfig = (aiCode: string) => {
  return get<AICustomizationConfigData>(
    "/api/v1/supplier/ai-product/customization/config",
    { params: { ai_code: aiCode } },
  );
};

// ============================================
// 3D 手办：图生模型（四视图 + 模型）
// ============================================

/** 创建图生模型任务请求 */
export interface CreateModelGenerationRequest {
  /** AI 应用生成记录 ID */
  ai_product_record_id: number;
}

/** 创建图生模型任务响应 */
export interface CreateModelGenerationResponse {
  /** 图生模型记录 ID，用于轮询 result */
  record_id?: number;
}

/** 查询图生模型结果 */
export interface GetModelGenerationResultResponse {
  ai_product_record_id?: number;
  create_at?: number;
  four_views_error?: string;
  four_views_result_urls?: string[];
  four_views_status?: number;
  model_error?: string;
  model_result_urls?: string[];
  model_status?: number;
  original_image_url?: string;
  record_id?: number;
  /** 整体状态：1=处理中,2=成功,3=失败 */
  status?: number;
}

/**
 * 创建图生模型任务（图 → 四视图 → 模型）
 */
export const createModelGeneration = (params: CreateModelGenerationRequest) => {
  return post<CreateModelGenerationResponse>(
    "/api/v1/supplier/ai-tripo3d-model-generation/create", // Tripo3D 单阶段接口：
    // "/api/v1/supplier/ai-model-generation/create",
    params,
  );
};

/**
 * 查询图生模型任务结果
 */
export const getModelGenerationResult = (recordId: number) => {
  return get<GetModelGenerationResultResponse>(
    // Tripo3D 单阶段接口："/api/v1/supplier/ai-tripo3d-model-generation/result"
    "/api/v1/supplier/ai-model-generation/result",
    { params: { record_id: recordId } },
  );
};

// ============================================
// 3D 手办：克重估算 / 定制报价
// ============================================

/** 提交 3D 克重估算请求 */
export interface Create3DWeightEstimationRequest {
  /** 最长边尺寸（cm） */
  max_edge: number;
  /** 图生模型记录 ID */
  model_generation_record_id: number;
  /** 规范化尺寸标签，例如 13.5cm */
  size_label: string;
  /** 重新报价时替换的旧报价记录 ID */
  replace_record_id?: number;
  /** 模型优化名称 */
  model_optimize_name?: string;
  /** 模型优化附加金额 */
  model_optimize_amount?: number;
}

export interface Create3DWeightEstimationResponse {
  /** 克重估算记录 ID，用于轮询 result */
  record_id?: number;
}

/** 查询 3D 克重估算结果 */
export interface Get3DWeightEstimationResultResponse {
  create_at?: number;
  error_msg?: string;
  /** 估价（元） */
  estimated_price?: number;
  max_edge?: number;
  model_optimize_name?: string;
  model_optimize_amount?: number;
  detect_status?: number;
  detect_result?: string;
  print_material_weight?: number;
  record_id?: number;
  size_label?: string;
  /** 状态：1=处理中,2=成功,3=失败 */
  status?: number;
  support_material_weight?: number;
  volume_a?: number;
  volume_b?: number;
}

/**
 * 提交 3D 克重估算任务（定制报价）
 */
export const create3DWeightEstimation = (
  params: Create3DWeightEstimationRequest,
) => {
  return post<Create3DWeightEstimationResponse>(
    "/api/v1/supplier/3d-weight-estimation/create",
    params,
  );
};

/**
 * 查询 3D 克重估算结果
 */
export const get3DWeightEstimationResult = (recordId: number) => {
  return get<Get3DWeightEstimationResultResponse>(
    "/api/v1/supplier/3d-weight-estimation/result",
    { params: { record_id: recordId } },
  );
};

/** 历史报价列表项 */
export interface WeightEstimationQueryItem {
  record_id: number;
  size_label: string;
  /** 状态：1=处理中,2=成功,3=失败 */
  status: number;
  estimated_price: number;
  max_edge: number;
  model_optimize_name?: string;
  model_optimize_amount?: number;
  detect_status?: number;
  detect_result?: string;
}

/**
 * 查询某个图生模型记录下的所有历史克重估算结果
 */
export const queryWeightEstimationHistory = (
  modelGenerationRecordId: number,
) => {
  return get<{ list: WeightEstimationQueryItem[] }>(
    "/api/v1/supplier/3d-weight-estimation/query",
    { params: { model_generation_record_id: modelGenerationRecordId } },
  );
};

// ============================================
// 手机壳专属接口
// ============================================

/** 手机壳模板图片响应 */
export interface PhoneCaseTemplateImageResponse {
  phone_model?: string;
  image_url?: string;
}

/**
 * 查询手机壳模板图片 URL（按手机型号）
 * 400 表示不支持的型号
 */
export const getPhoneCaseTemplateImage = (phoneModel: string) => {
  return get<PhoneCaseTemplateImageResponse>(
    "/api/v1/supplier/ai-product/phone-case/template-image",
    { params: { phone_model: phoneModel } },
  );
};

/**
 * 编辑手机壳效果图（替换记录的 effect_image_url）
 */
export const updatePhoneCaseEffectImage = (params: {
  record_id: number;
  effect_image_url: string;
  img_x?: number;
  img_y?: number;
  img_w?: number;
  img_h?: number;
  rotation?: number;
}) => {
  return post<null>(
    "/api/v1/supplier/ai-product/phone-case/record/update-effect",
    params,
  );
};

/** 手机壳 AI 补全响应 */
export interface PhoneCaseAICompleteResponse {
  task_id?: string;
  /** 与入参 record_id 一致或后端指定的生成记录 ID，用于 ids 轮询 */
  record_id?: number;
}

/**
 * 手机壳 AI 补全（基于已有记录重新生成平面图+效果图）
 */
export const phoneCaseAIComplete = (params: { record_id: number }) => {
  return post<PhoneCaseAICompleteResponse>(
    "/api/v1/supplier/ai-product/phone-case/ai-complete",
    params,
  );
};

// ============================================
// 我的商品 API
// ============================================

/** 手办/冰箱贴 extra（type_code 1/2） */
export interface MyProductExtraFigurine {
  generate_image_url?: string;
  generated_images?: {
    draft?: string;
    effect?: string;
  };
}

/** 平面商品 extra（type_code 6） */
export interface MyProductExtraApparel {
  front_product_image_url?: string;
  back_product_image_url?: string;
  front_custom_area_image_url?: string;
  back_custom_area_image_url?: string;
  front_region_px?: { w?: number; h?: number };
  back_region_px?: { w?: number; h?: number };
  front_size_cm?: { w?: number; h?: number };
  back_size_cm?: { w?: number; h?: number };
  front_print_size_cm?: { w?: number; h?: number };
  back_print_size_cm?: { w?: number; h?: number };
  /** 与生成记录详情 extra 一致：正/背面用户原素材 */
  front_original_image_urls?: string[];
  back_original_image_urls?: string[];
}

/** 手机壳 extra（type_code 5） */
export interface MyProductExtraPhonecase {
  generate_image_url?: string;
  generated_images?: {
    effect?: string;
    plane?: string;
    psd?: string;
  };
  /** 图片模式为图片描述，文字模式为生成文案（与生成记录 extra 一致） */
  user_prompt?: string;
  phone_model?: string;
  template_w?: number;
  template_h?: number;
  canvas_w?: number;
  canvas_h?: number;
  tpl_x?: number;
  tpl_y?: number;
  img_x?: number;
  img_y?: number;
  img_w?: number;
  img_h?: number;
  rotation?: number;
}

export type MyProductExtra =
  | MyProductExtraFigurine
  | MyProductExtraFridgeMagnet
  | MyProductExtraApparel
  | MyProductExtraPhonecase;

/** 冰箱贴 extra（type_code 2/8-16） */
export interface MyProductExtraFridgeMagnet extends MyProductExtraFigurine {
  engraving_texts?: string[];
}

/** 我的商品列表项（AIMyProductListItem） */
export interface MyProductListItem {
  /** 生成记录ID */
  id: number;
  /** 记录状态：1-处理中 2-成功 3-失败 */
  status: number;
  /** 商品类型：1-3D手办 2-冰箱贴 5-手机壳 6-平面商品 */
  type_code: number;
  /** 商品类型名称 */
  type_name: string;
  /** 模型唯一标识 */
  model_id_str: string;
  /** 模型名称 */
  model_name: string;
  /** 商品SKU，可直接用于加入采购车 */
  sku: string;
  /** 商品SPU */
  spu?: string;
  /**
   * 生成商品图：
   * 3D/冰箱贴=[效果图]；手机壳=[平面图,效果图]；平面商品=[正面,背面]
   */
  product_images: string[];
  /** 线稿图：3D/冰箱贴=[单图]，其余为空 */
  draft_images: string[];
  /** 原始图URL数组 */
  original_image_urls: string[];
  /** 类型差异化数据，结构按 type_code 不同而异 */
  extra: MyProductExtra | null;
  /** 最新图生模型记录ID，0表示无记录（仅3D手办有意义） */
  /** 图生模型记录ID，0表示无记录（仅3D手办有意义） */
  model_generation_record_id: number;
  /** 生成记录创建时间（Unix毫秒） */
  create_at: number;
  /** 生成/设计完成时间（Unix毫秒），列表「设计时间」优先展示 */
  complete_at?: number;
  /** 加入我的商品时间（Unix毫秒） */
  my_product_at: number;
  /** 关联商品名称 */
  product_name?: string;
}

/** 我的商品类型数量统计 */
export interface MyProductTypeCount {
  type_code: number;
  type_name: string;
  count: number;
}

/** 我的商品列表请求参数 */
export interface MyProductListParams {
  page: number;
  page_size: number;
  /** 商品类型过滤，不传则查全部 */
  type_code?: number[];
  /** 商品名称，模糊搜索 */
  product_name?: string;
}

/** 我的商品列表响应 data */
export interface MyProductListData {
  total: number;
  list: MyProductListItem[];
  /** 各类型数量统计（只返回有数据的类型） */
  type_counts: MyProductTypeCount[];
}

/**
 * 查询我的商品列表（GET）
 */
export const getMyProductList = (params: MyProductListParams) => {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page));
  searchParams.set("page_size", String(params.page_size));
  params.type_code?.forEach((typeCode) => {
    searchParams.append("type_code", String(typeCode));
  });
  const productName = params.product_name?.trim();
  if (productName) {
    searchParams.set("product_name", productName);
  }
  return get<MyProductListData>(
    `/api/v1/supplier/ai-product/my-product/list?${searchParams.toString()}`,
  );
};

/**
 * 保存至我的商品
 */
export const addMyProduct = (params: { record_id: number }) => {
  return post<null>("/api/v1/supplier/ai-product/my-product/add", params);
};

/**
 * 从我的商品中移除
 */
export const removeMyProduct = (params: { record_id: number }) => {
  return post<null>("/api/v1/supplier/ai-product/my-product/remove", params);
};
